import React, { ReactNode } from 'react'
import { NavigateFn } from '@reach/router'
import {
  Button,
  Card,
  Flex,
  IconButton,
  PrettyDate,
  Confirmation,
  Toaster,
  PrettyNumber,
  EmployeeWithDetailsPopover,
  ColorSquare,
  GridTableStatusEnum,
  NewGridTable,
  IGridTableColumn,
  Icon,
  Popover,
} from '@ui'
import numeral from 'numeral'
import { UseWorkplaceGroupsHook } from './hooks/useWorkplaceGroups'
import api, { IWorkplaceGroupTableRecord } from '@api'
import { pluralize } from '@utils'
import { Classes, Intent, Menu, MenuItem, Position } from '@blueprintjs/core'
import { ISelection } from '@hooks/useSelection'
import { ApolloError } from 'apollo-boost'
import { useReload } from '@hooks/reloadContext'
import track from '@track'
import Event from '@track/Event'
import classnames from 'classnames'
import { useFileDownload } from '@hooks'

interface TableViewProps {
  workplaceGroups: UseWorkplaceGroupsHook
  navigate?: NavigateFn
}

export default function TableView(props: TableViewProps) {
  const { workplaceGroups, navigate } = props

  const columns: IGridTableColumn<IWorkplaceGroupTableRecord>[] = [
    {
      label: 'Name',
      sort: 'name',
      Cell: ({ cell, skeleton }) => {
        return (
          <div
            className={classnames('flex items-center group cursor-pointer', {
              [Classes.SKELETON]: skeleton,
            })}
            onClick={() => {
              if (cell) navigate?.(`?workplaceGroupId=${cell.data.id}`)
              track.workplaceGroups({ eventName: Event.ROW_CLICK })
            }}
          >
            {cell ? (
              <>
                <ColorSquare
                  style={{
                    background: `#${cell.data.color}`,
                    marginRight: 10,
                  }}
                />
                <div className={classnames('ml-2 font-medium text-base group-hover:underline')}>
                  {cell.data.name}
                </div>
              </>
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      label: 'Seats Associated',
      alignRight: true,
      noWrapHeader: true,
      Cell: ({ cell }) =>
        cell ? (
          <PrettyNumber
            value={cell.data.totals.spaces}
            data-input={`${cell.data.name}_spaces_associated`}
          />
        ) : (
          <div className={Classes.SKELETON}>place</div>
        ),
    },
    {
      label: 'Employees Associated',
      alignRight: true,
      noWrapHeader: true,
      Cell: ({ cell }) =>
        cell ? (
          <PrettyNumber
            value={cell.data.totals.employees}
            data-input={`${cell.data.name}_employees_associated`}
          />
        ) : (
          <div className={Classes.SKELETON}>place</div>
        ),
    },
    {
      label: 'Created By/Date',
      sort: 'createdAt',
      noWrapHeader: true,
      Cell: ({ cell }) =>
        cell ? (
          <Flex style={{ color: 'var(--grey1)' }} align="center">
            {cell.data.createdBy && (
              <>
                <EmployeeWithDetailsPopover
                  buttonProps={{ small: true, minimal: true }}
                  employee={cell.data.createdBy.employee}
                />
                <span style={{ paddingRight: '0.4em', fontSize: 17 }}>•</span>
              </>
            )}
            <div style={{ paddingLeft: cell.data.createdBy ? 0 : '7px' }}>
              <PrettyDate value={cell.data.createdAt} timeStringFormat={'YYYY-MM-DD hh:mm:ss'} />
            </div>
          </Flex>
        ) : (
          <div className={Classes.SKELETON}>place</div>
        ),
    },
  ]

  const format = (items: IWorkplaceGroupTableRecord[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formated = format(workplaceGroups.items)

  return (
    <>
      <Card.Header>
        <Toolbar
          {...{
            navigate,
            workplaceGroups,
          }}
        />
      </Card.Header>
      <NewGridTable<IWorkplaceGroupTableRecord>
        status={
          workplaceGroups.isLoading
            ? GridTableStatusEnum.LOADING
            : workplaceGroups.isSorting
            ? GridTableStatusEnum.SORTING
            : workplaceGroups.isPaging
            ? GridTableStatusEnum.PAGING
            : GridTableStatusEnum.IDLE
        }
        minCellWidth="max-content"
        columns={columns}
        data={formated}
        selection={workplaceGroups.selection}
        sort={{
          column: workplaceGroups.sort.column,
          order: workplaceGroups.sort.order,
          onSort: (column) => {
            workplaceGroups.selection.clear()
            workplaceGroups.sortBy(column)
            track.workplaceGroups({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        page={{
          hasNextPage: workplaceGroups.pageInfo.hasNextPage,
          endCursor: workplaceGroups.pageInfo.endCursor,
          onNextPage: () => {
            workplaceGroups.nextPage()
            track.workplaceGroups({
              eventName: Event.PAGE,
            })
          },
        }}
        skeletonSize={5}
        track={{
          select: () =>
            track.workplaceGroups({
              eventName: Event.ROW_SELECT,
            }),
          selectAll: () =>
            track.workplaceGroups({
              eventName: Event.SELECT_ALL,
            }),
        }}
        headerTop={52}
      />
    </>
  )
}

function Toolbar({
  workplaceGroups,
  navigate,
}: {
  workplaceGroups: UseWorkplaceGroupsHook
  navigate?: NavigateFn
}) {
  let helperText = `${workplaceGroups.count} ${pluralize(workplaceGroups.count, 'record')}`
  let clearButton: ReactNode = null
  const selectedWorkplaceGroups = workplaceGroups.items.filter(
    (e) => workplaceGroups.selection.items[e.id]
  )
  const startFileDownload = useFileDownload()

  if (workplaceGroups.selection.count) {
    helperText = `${workplaceGroups.selection.count} selected`
    clearButton = (
      <IconButton
        minimal
        tooltip="Clear selection"
        iconName="times"
        style={{ marginLeft: 4 }}
        data-button="clear_selection"
        onClick={() => {
          workplaceGroups.selection.clear()
          track.workplaceGroups({
            eventName: Event.CLEAR_SELECTION,
          })
        }}
      />
    )
  } else if (workplaceGroups.query) {
    helperText = `${numeral(workplaceGroups.count).format('0,0')} filtered record${
      workplaceGroups.count === 1 ? '' : 's'
    }`

    clearButton = (
      <IconButton
        minimal
        tooltip="Clear filter"
        iconName="times"
        style={{ marginLeft: 4 }}
        onClick={() => {
          workplaceGroups.clearQuery()
          track.workplaceGroups({
            eventName: Event.CLEAR_ALL_FILTERS,
          })
        }}
      />
    )
  }

  return (
    <>
      <Flex align="center" justify="space-between" style={{ width: '98%' }}>
        <Flex align="baseline" style={{ marginLeft: 10 }}>
          {workplaceGroups.selection.count !== 0 && (
            <div style={{ marginRight: 10 }}>
              <MultiDeleteButton
                selectedWorkplaceGroups={selectedWorkplaceGroups}
                selection={workplaceGroups.selection}
                onConfirm={() => {
                  workplaceGroups.selection.clear()
                  workplaceGroups.optimisticDelete(Object.keys(workplaceGroups.selection.items))
                }}
              />
            </div>
          )}
          {workplaceGroups.isLoading ? <Skeleton /> : helperText}
          {clearButton}
        </Flex>
        <Flex>
          <Button
            data-button="new_workplace_group"
            iconName="plus"
            text="New workplace group"
            onClick={() => {
              navigate?.('?newItem=workplaceGroup')
              track.workplaceGroups({
                eventName: Event.NEW_RECORD_DIALOG,
              })
            }}
          />
          <Popover
            position={Position.BOTTOM}
            content={
              <Menu>
                <MenuItem
                  text="Download CSV"
                  icon={<Icon style={{ marginTop: 2 }} name="download" />}
                  onClick={() => {
                    workplaceGroups.fetchExportLink().then((url) => startFileDownload(url))
                  }}
                />
              </Menu>
            }
          >
            <IconButton
              round={false}
              className="ml2"
              tooltip="More actions"
              iconName="ellipsis-h"
            />
          </Popover>
        </Flex>
      </Flex>
    </>
  )
}

function Skeleton() {
  return <span className={Classes.SKELETON}>Placeholder text</span>
}

function MultiDeleteButton({
  disabled,
  selectedWorkplaceGroups,
  selection,
  onConfirm,
}: {
  disabled?: boolean
  selectedWorkplaceGroups: IWorkplaceGroupTableRecord[]
  selection: ISelection
  onConfirm?: () => void
}) {
  const reload = useReload()

  return (
    <Confirmation
      disabled={disabled}
      text={`Delete selected workplace group${pluralize(selectedWorkplaceGroups.length)}?`}
      onConfirm={() => {
        onConfirm?.()
        Promise.all(selectedWorkplaceGroups.map((e) => api.workplaceGroup.remove(e.id)))
          .then(() => {
            selection.clear()
            reload.workplaceGroups()
            Toaster.success({
              message: `${selectedWorkplaceGroups.length} ${pluralize(
                selectedWorkplaceGroups.length,
                'workplace group'
              )} deleted`,
            })
          })
          .catch((error: ApolloError) => {
            Toaster.fail({
              error,
            })
          })

        track.workplaceGroups({
          eventName: Event.BATCH_ACTION,
          props: {
            actionName: 'Remove workplace groups',
            actionValue: null,
            batchCount: selectedWorkplaceGroups.length,
          },
        })
      }}
    >
      <Button
        ghost
        data-button="delete_workplace_groups"
        disabled={disabled}
        intent={Intent.DANGER}
        iconName="trash"
        text="Delete"
      />
    </Confirmation>
  )
}
